import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, Download } from 'lucide-react';
import { PageHeader } from '@/components/layout/AppShell';
import { ErrorState } from '@/components/common/ErrorState';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Field, Input, Select } from '@/components/ui/primitives';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useReports } from '@/hooks/queries';
import { formatDateTime, toDateTimeInput } from '@/lib/format';
import { mytagApi } from '@/services/mytagApi';

export function ReportsPage(): JSX.Element {
  const online = useOnlineStatus();
  const reports = useReports();
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: mytagApi.campaigns });
  const [campaignId, setCampaignId] = useState('');
  const [deviceSelected, setDeviceSelected] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tester, setTester] = useState('');
  const [notes, setNotes] = useState('');
  const selectedCampaign = useMemo(
    () => campaigns.data?.campaigns.find((campaign) => campaign.id === campaignId) ?? null,
    [campaignId, campaigns.data],
  );
  const download = useMutation({ mutationFn: mytagApi.downloadCdcReport });

  useEffect(() => {
    if (campaignId || !campaigns.data?.campaigns[0]) return;
    setCampaignId(campaigns.data.campaigns[0].id);
  }, [campaignId, campaigns.data]);

  useEffect(() => {
    if (!selectedCampaign) return;
    setFrom(toDateTimeInput(selectedCampaign.startsAt));
    setTo(toDateTimeInput(selectedCampaign.endsAt));
    setTester(selectedCampaign.tester);
    setDeviceSelected(true);
    setNotes('');
    download.reset();
  }, [selectedCampaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const executed = selectedCampaign?.scenarios.filter((scenario) => scenario.result !== 'pending').length ?? 0;
  const canDownload = Boolean(selectedCampaign && deviceSelected && from && to && tester.trim() && online && !download.isPending);

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!selectedCampaign || !canDownload) return;
    download.mutate({
      campaignId: selectedCampaign.id,
      deviceNums: [selectedCampaign.deviceNum],
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
      tester: tester.trim(),
      notes,
    });
  }

  return (
    <>
      <PageHeader title="Rapports" description="Rapports CDC Word éditables générés depuis les preuves locales persistées, sans nouvel appel Vernal." />

      <Card>
        <CardHeader>
          <CardTitle>Rapport de validation technique — SmartTags F8</CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.isError ? <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} title="Campagnes indisponibles" /> : null}
          {!campaigns.isLoading && !campaigns.isError && !campaigns.data?.campaigns.length ? (
            <EmptyState icon={<ClipboardCheck className="h-5 w-5" />} title="Aucune campagne accessible" description="Créez une campagne terrain avant de produire un rapport CDC." />
          ) : null}
          {campaigns.data?.campaigns.length ? (
            <form className="space-y-4" onSubmit={submit}>
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Campagne" htmlFor="cdc-campaign">
                  <Select id="cdc-campaign" value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
                    {campaigns.data.campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
                  </Select>
                </Field>
                <Field label="Appareil(s) de la campagne">
                  <label className="flex min-h-10 items-center gap-3 rounded-md border border-input bg-card px-3 text-sm">
                    <input type="checkbox" checked={deviceSelected} onChange={(event) => setDeviceSelected(event.target.checked)} />
                    <span className="min-w-0 break-all">{selectedCampaign?.deviceNum}</span>
                  </label>
                </Field>
                <Field label="Du" htmlFor="cdc-from"><Input id="cdc-from" type="datetime-local" required value={from} onChange={(event) => setFrom(event.target.value)} /></Field>
                <Field label="Au" htmlFor="cdc-to"><Input id="cdc-to" type="datetime-local" required value={to} onChange={(event) => setTo(event.target.value)} /></Field>
                <Field label="Testeur" htmlFor="cdc-tester"><Input id="cdc-tester" required maxLength={100} value={tester} onChange={(event) => setTester(event.target.value)} /></Field>
                <Field label="État de la campagne">
                  <div className="min-h-10 rounded-md bg-muted px-3 py-2 text-sm">{selectedCampaign?.status ?? '—'} · {executed}/{selectedCampaign?.scenarios.length ?? 0} scénario(s) exécuté(s)</div>
                </Field>
              </div>
              <Field label="Notes complémentaires" htmlFor="cdc-notes" hint="Ces notes sont incluses dans le document ; ne saisissez aucun secret.">
                <textarea id="cdc-notes" maxLength={5000} rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} className="focus-ring w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground" />
              </Field>
              {!online ? <Alert tone="warning" title="Téléchargement désactivé hors connexion">L’export n’est ni mis en attente ni servi depuis le cache.</Alert> : null}
              {download.isError ? <ErrorState error={download.error} title="Le rapport Word n’a pas pu être généré" /> : null}
              {download.isSuccess ? (
                <Alert tone="success" title={download.data.delivery === 'android-share' ? 'Rapport Word prêt à enregistrer' : 'Rapport Word téléchargé'}>
                  <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Fichier : {download.data.filename}</p>
                  {download.data.delivery === 'android-share' ? <p className="mt-1 text-xs">Le menu Android permet de l’enregistrer ou de le partager. Une copie temporaire privée sera supprimée automatiquement.</p> : null}
                </Alert>
              ) : null}
              <Button type="submit" disabled={!canDownload} loading={download.isPending} icon={<Download className="h-4 w-4" />}>Télécharger le rapport Word</Button>
            </form>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Rapports de mesures locaux existants</CardTitle></CardHeader>
        <CardContent>
          {reports.data?.reports.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {reports.data.reports.map((report) => (
                <div key={report.id} className="rounded-md border border-border p-3">
                  <p className="font-medium">{report.status}</p>
                  <p className="break-all text-xs text-muted-foreground">{report.deviceNum} · {formatDateTime(report.createdAt)}</p>
                  <p className="mt-2 text-sm">{String(report.metrics.positions ?? 0)} position(s), précision moyenne {report.metrics.averageAccuracy === null || report.metrics.averageAccuracy === undefined ? 'Non disponible' : String(report.metrics.averageAccuracy)}</p>
                  {report.notes ? <p className="mt-2 text-sm text-muted-foreground">{report.notes}</p> : null}
                </div>
              ))}
            </div>
          ) : <EmptyState icon={<ClipboardCheck className="h-5 w-5" />} title="Aucun rapport de mesures" description="Le rapport CDC reste disponible avec les éléments présents dans la campagne." />}
        </CardContent>
      </Card>
    </>
  );
}
